import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem('access_token'));
  
  // Use ref to prevent StrictMode double-initialization
  const initRef = useRef(false);
  const processingGoogleRef = useRef(false);

  // Initialize auth on mount - with StrictMode protection
  useEffect(() => {
    // Prevent double initialization in StrictMode
    if (initRef.current) {
      return;
    }
    initRef.current = true;
    
    initializeAuth();
    
    // Cleanup function for StrictMode
    return () => {
      initRef.current = false;
    };
  }, []);

  const initializeAuth = async () => {
    console.log('[Auth] Starting initialization...');
    
    try {
      // Check if we have a Google OAuth callback
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        if (sessionId) {
          console.log('[Auth] Found session_id in URL, processing Google login...');
          await processGoogleSession(sessionId);
          return;
        }
      }

      // Check if we have a stored token
      const token = localStorage.getItem('access_token');
      if (token) {
        console.log('[Auth] Found stored token, validating...');
        await checkAuthWithToken(token);
      } else {
        console.log('[Auth] No stored token found');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('[Auth] Initialization failed:', err);
      clearAuth();
      setIsLoading(false);
    }
  };

  const checkAuthWithToken = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('[Auth] Token valid, user authenticated:', userData.email);
        setUser(userData);
        setAccessToken(token);
      } else {
        console.log('[Auth] Token invalid, clearing auth');
        clearAuth();
      }
    } catch (err) {
      console.error('[Auth] Token validation failed:', err);
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const processGoogleSession = async (sessionId) => {
    // Prevent double processing
    if (processingGoogleRef.current) {
      console.log('[Auth] Already processing Google session, skipping...');
      return;
    }
    processingGoogleRef.current = true;
    
    try {
      setError(null);
      console.log('[Auth] Processing Google session:', sessionId);
      
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Google login failed' }));
        throw new Error(errorData.detail || 'Failed to process Google login');
      }

      const data = await response.json();
      console.log('[Auth] Google session processed successfully');
      
      // Store token
      const token = data.access_token || data.session_token;
      if (token) {
        localStorage.setItem('access_token', token);
        setAccessToken(token);
        console.log('[Auth] Token stored');
      }
      
      // Set user data
      if (data.user) {
        setUser(data.user);
        console.log('[Auth] User set:', data.user.email);
      }
      
      // Clear URL hash AFTER setting state
      window.history.replaceState(null, '', window.location.pathname);
      console.log('[Auth] URL hash cleared');
      
    } catch (err) {
      setError(err.message);
      console.error('[Auth] Google session processing failed:', err);
      clearAuth();
    } finally {
      setIsLoading(false);
      processingGoogleRef.current = false;
    }
  };

  const login = useCallback(async (email, password) => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[Auth] Attempting login for:', email);
      
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();
      console.log('[Auth] Login successful');
      
      // Store tokens FIRST
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        setAccessToken(data.access_token);
      }
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      
      // THEN set user - this triggers the re-render that shows dashboard
      setUser(data.user);
      
      return true;
    } catch (err) {
      console.error('[Auth] Login failed:', err);
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signup = useCallback(async (email, password, name) => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[Auth] Attempting signup for:', email);
      
      const response = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Signup failed');
      }

      const data = await response.json();
      console.log('[Auth] Signup successful');
      
      // Store tokens
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        setAccessToken(data.access_token);
      }
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      
      // Set user
      setUser(data.user);
      
      return true;
    } catch (err) {
      console.error('[Auth] Signup failed:', err);
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginWithGoogle = useCallback(() => {
    const currentUrl = window.location.origin;
    console.log('[Auth] Redirecting to Google login, return URL:', currentUrl);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(currentUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    console.log('[Auth] Logging out...');
    
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });
      }
    } catch (err) {
      console.error('[Auth] Logout API call failed:', err);
    }
    
    clearAuth();
    console.log('[Auth] Logged out successfully');
  }, []);

  const clearAuth = () => {
    setUser(null);
    setAccessToken(null);
    setError(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  };

  const clearError = useCallback(() => setError(null), []);

  // Helper function for authenticated API calls
  const authFetch = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('access_token');
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${url}`, {
      ...options,
      headers,
      credentials: 'include'
    });

    // If unauthorized on non-auth endpoint, clear auth
    if (response.status === 401) {
      const isAuthEndpoint = url.includes('/auth/');
      if (!isAuthEndpoint) {
        console.log('[Auth] Received 401 on API call, clearing auth');
        clearAuth();
      }
    }

    return response;
  }, []);

  const value = {
    user,
    isLoading,
    error,
    isAuthenticated: !!user,
    login,
    signup,
    loginWithGoogle,
    logout,
    clearError,
    authFetch,
    accessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
